import * as React from 'react'
import { useSelector } from 'react-redux'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Ellipsis } from 'lucide-react'

import DeleteNotetakerDataDialog from './DeleteNotetakerDataDialog'
import RemoveMemberDialog from './RemoveMemberDialog'

import { isApiError } from '@/api'
import { moveMemberToTeam } from '@/api/members'
import { type EnterpriseMemberDetailResponse } from '@/api/teamMembersTable'
import { useToast } from '@/components/Toast/ToastContext'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAdminNotetakerErasureEnabled } from '@/hooks/useAdminNotetakerErasureEnabled'
import { useEnterpriseTeams } from '@/hooks/useEnterpriseTeams'
import { hasDepartments } from '@/hooks/useHasDepartments'
import { MY_ACTIVE_COST_CENTER_QUERY_KEY } from '@/pages/app/Team/Department/utils'
import { getRemovalUsageLine } from '@/pages/app/Team/removalUsageLine'
import MemberRoleSelect from '@/pages/app/UsageV2/MemberRoleSelect'
import { selectCurrentEnterprise } from '@/store/features/user/userSlice'

interface MemberDetailActionsProps {
  member: EnterpriseMemberDetailResponse
  onRemoved: () => void
}

const fullName = (m: EnterpriseMemberDetailResponse): string => {
  const first = m.firstName ?? ''
  const last = m.lastName ?? ''
  const joined = `${first} ${last}`.trim()
  return joined || m.email || m.userId
}

const MemberDetailActions = ({ member, onRemoved }: MemberDetailActionsProps) => {
  const enterprise = useSelector(selectCurrentEnterprise)
  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const [removeOpen, setRemoveOpen] = React.useState(false)
  const [deleteNotetakerOpen, setDeleteNotetakerOpen] = React.useState(false)
  const notetakerErasureEmail = useAdminNotetakerErasureEnabled() ? member.email : null

  const teamsQuery = useEnterpriseTeams(enterprise?.id ?? '')

  const teamMutation = useMutation({
    mutationFn: (nextTeamId: string) => moveMemberToTeam(nextTeamId, { user_id: member.userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] })
      // Member movement shifts the CC-wide picker + caller's-team metadata
      // used by the Create-Department successor-admin check.
      queryClient.invalidateQueries({ queryKey: [MY_ACTIVE_COST_CENTER_QUERY_KEY] })
    },
    onError: (error: unknown) => {
      const message = isApiError(error) ? error.message : 'Failed to move member'
      updateToast(message, 'error')
    },
  })

  const currentTeamId = member.teamId ?? undefined

  return (
    <>
      <Select
        value={currentTeamId}
        onValueChange={(v) => {
          if (v === currentTeamId || teamMutation.isPending) return
          teamMutation.mutate(v)
        }}
        disabled={!enterprise || teamMutation.isPending}
      >
        <SelectTrigger className="min-w-[140px]">
          <SelectValue placeholder={member.teamName ?? 'No team'} />
        </SelectTrigger>
        <SelectContent>
          {teamsQuery.data?.map((team) => (
            <SelectItem key={team.id} value={team.id}>
              {team.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <MemberRoleSelect userId={member.userId} currentRole={member.role ?? null} />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" aria-label="More actions">
            <Ellipsis className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={notetakerErasureEmail ? 'w-52' : 'w-40'}>
          {notetakerErasureEmail && (
            <DropdownMenuItem onSelect={() => setDeleteNotetakerOpen(true)}>Delete Notetaker data</DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => setRemoveOpen(true)}>Remove</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {notetakerErasureEmail && (
        <DeleteNotetakerDataDialog
          userId={member.userId}
          email={notetakerErasureEmail}
          displayName={fullName(member)}
          open={deleteNotetakerOpen}
          onOpenChange={setDeleteNotetakerOpen}
        />
      )}

      <RemoveMemberDialog
        userId={member.userId}
        displayName={fullName(member)}
        usageLine={getRemovalUsageLine(member)}
        scope={hasDepartments(enterprise) ? 'organization' : 'team'}
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        onSuccess={onRemoved}
      />
    </>
  )
}

export default MemberDetailActions
